import { Formik, Form } from "formik";
import DynamicField from "./DynamicField";
import styles from "./DynamicForm.module.css";
import * as Yup from "yup";
import { Field as FieldConfig } from "../../types/types.form";
import { useLanguage } from "../../context/LanguageContext";
import { useState } from "react";
import Toast from "../Toast/Toast";

interface DynamicFormProps<T extends Record<string, unknown>> {
  config?: FieldConfig[];
  validationSchema?: Yup.ObjectSchema<Record<string, unknown>> | null;
  onSubmit?: (values: T) => void;
  showDataAlert?: boolean;
  showToast?: boolean;
  requireDirty?: boolean;
}

interface ToastState {
  message: string;
  type: "success" | "error" | "info";
}

export default function DynamicForm<T extends Record<string, unknown>>({
  config = [],
  validationSchema = null,
  onSubmit,
  showDataAlert = false,
  showToast: showToastProp = false,
  requireDirty = false,
}: DynamicFormProps<T>) {

  const { t } = useLanguage();
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" | "info" = "success"
  ) => {
    if (showToastProp) {
      setToast({ message, type });
    }
  };

  const initialValues: Record<string, unknown> = config.reduce((acc, field) => {
    acc[field.name] =
      field.defaultValue !== undefined
        ? field.defaultValue
        : field.type === "multiselect"
        ? []
        : "";
    return acc;
  }, {} as Record<string, unknown>);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
        onSubmit={(values, actions) => {
          try {
            if (showToastProp) {
              showToast(t("dataSavedSuccessfully"), "success");
            }

            if (showDataAlert) {
              setTimeout(() => {
                alert(JSON.stringify(values, null, 2));
              }, 500);
            }

            if (onSubmit) {
              onSubmit(values as T);
            }

            console.log("Форма отправлена:", values);

          } catch (error) {
            console.error("Ошибка отправки формы:", error);
            if (showToastProp) {
              showToast(t("errorSavingData"), "error");
            }
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, isValid, dirty, submitForm }) => {
          const isFormValid = requireDirty ? (isValid && dirty) : isValid;

          return (
            <Form
              className={styles.form}

              onKeyDown={(e) => {
                const tag = (e.target as HTMLElement).tagName.toLowerCase();
                const isTextField = ["input", "textarea"].includes(tag);

                if (e.key === "Enter" && isTextField) {
                  e.preventDefault(); 
                  submitForm();
                }
              }}
            >
              {config.map((field) => (
                <div key={field.name} className={styles.field}>
                  <DynamicField field={field} />
                </div>
              ))}

              <button
                type="submit"
                className={styles.button}
                disabled={isSubmitting || !isFormValid}
              >
                {isSubmitting ? t("saving") : t("save")}
              </button>
            </Form>
          );
        }}
      </Formik>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
